import { Injectable } from '@angular/core';
import { HttpService } from '../service/http.service';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

export interface BilingualText {
  ar: string;
  en: string;
}

export interface VendorProfile {
  id: number;
  user_id: number;
  name: BilingualText;
  description: BilingualText;
  tagline: string | null;
  type: string;
  average_price: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude: string;
  longitude: string;
  owner_firstname: string;
  owner_lastname: string;
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  phone: string;
  email: string;
  website: string;
  logo: string;
  banner_image: string;
  admin_approval: string;
  status: string;
  instagram_url: string | null;
  facebook_url: string | null;
  twitter_url: string | null;
  linkedin_url: string | null;
  youtube_url: string | null;
  tiktok_url: string | null;
  pinterest_url: string | null;
  snapchat_url: string | null;
  website_url: string | null;
  google_map_url: string | null;
  open_time: string;
  close_time: string;
  preparation_time: string;
  avarage_rating: number | null;
  total_reviews: number | null;
  admin_fee_percentage: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  total_orders: number | null;
  total_earning: number | string | null;
  user_details?: {
    id: number;
    user_id: string;
    address: string;
    city: string;
    town: string;
    [key: string]: any;
  };
}

export interface VendorProfileResponse {
  success: boolean;
  data: VendorProfile;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {

  constructor(private http: HttpService) { }

  getProfile(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/contractor/get-profile`);
  }

  /**
   * Get vendor/store profile details
   * Endpoint: GET /api/dp/my-vendor-detail
   * Returns: VendorProfileResponse with vendor profile data
   */
  getVendorProfile(): Observable<VendorProfileResponse> {
    return this.http.get(`${environment.apiBaseUrl}/dp/my-vendor-detail`);
  }

  /**
   * Convert store profile payload to FormData object
   * Handles multi-language fields, simple fields, and file uploads
   * 
   * Expected payload structure:
   * {
   *   storeName: string,
   *   storeCategory: string,
   *   shortDescription: string,
   *   address: string,
   *   openTime: string,
   *   closeTime: string,
   *   preparationTime: string,
   *   storeEmail: string,
   *   owner_firstname: string,
   *   owner_lastname: string,
   *   phone: string,
   *   logoFile?: File,
   *   bannerFile?: File
   * }
   */
  convertStorePayloadToFormData(payload: any, logoFile?: File, bannerFile?: File): FormData {
    const formData = new FormData();

    // Multi-language name field (requires both en and ar)
    // Currently using English, can be extended for Arabic
    if (payload.storeName) {
      formData.append('name[en]', payload.storeName);
      formData.append('name[ar]', payload.storeName); // TODO: Add Arabic translation if available
    }

    // Multi-language description field
    if (payload.shortDescription) {
      formData.append('description[en]', payload.shortDescription);
      formData.append('description[ar]', payload.shortDescription); // TODO: Add Arabic translation if available
    }

    // Simple string fields - map from form field names to API field names
    const fieldMappings: { [key: string]: string } = {
      storeCategory: 'type',
      address: 'address_line_1',
      storeEmail: 'email',
      openTime: 'open_time',
      closeTime: 'close_time',
      preparationTime: 'preparation_time'
    };

    // Additional fields that map directly
    const directFields = ['owner_firstname', 'owner_lastname', 'phone'];

    // Apply field mappings
    Object.entries(fieldMappings).forEach(([formField, apiField]) => {
      if (payload[formField]) {
        formData.append(apiField, payload[formField]);
      }
    });

    // Apply direct fields (same name in form and API)
    directFields.forEach(field => {
      if (payload[field]) {
        formData.append(field, payload[field]);
      }
    });

    // Optional fields with default values or predefined values
    const optionalFields: { [key: string]: string | number } = {
      address_line_2: payload.address_line_2 || '',
      city: payload.city || '',
      state: payload.state || '',
      country: payload.country || '',
      postal_code: payload.postal_code || '0000',
      latitude: payload.latitude || '0',
      longitude: payload.longitude || '0',
      average_price: payload.average_price || ''
    };

    Object.entries(optionalFields).forEach(([field, value]) => {
      if (value) {
        formData.append(field, value.toString());
      }
    });

    // Handle file uploads
    if (logoFile instanceof File) {
      formData.append('logo', logoFile);
    }

    if (bannerFile instanceof File) {
      formData.append('banner_image', bannerFile);
    }

    return formData;
  }

  updateVendor(vendorId: number, storePayload: any, logoFile?: File, bannerFile?: File): Observable<any> {
    const formData = new FormData();

    // Multi-language name field (requires both en and ar)
    if (storePayload.storeName) {
      formData.append('name[en]', storePayload.storeName);
      formData.append('name[ar]', storePayload.storeName); // TODO: Add Arabic translation if available
    }

    // Multi-language description field
    if (storePayload.shortDescription) {
      formData.append('description[en]', storePayload.shortDescription);
      formData.append('description[ar]', storePayload.shortDescription); // TODO: Add Arabic translation if available
    }

    // Simple string fields - map from form field names to API field names
    const fieldMappings: { [key: string]: string } = {
      storeCategory: 'type',
      storePhone: 'phone',
      openTime: 'open_time',
      closeTime: 'close_time',
      preparationTime: 'preparation_time'
    };

    // Apply field mappings
    Object.entries(fieldMappings).forEach(([formField, apiField]) => {
      if (storePayload[formField]) {
        formData.append(apiField, storePayload[formField]);
      }
    });

    // Always append optional fields with fallback values (same pattern as updateProductData)
    formData.append('address_line_1', storePayload.address_line_1 || '');
    formData.append('address_line_2', storePayload.address_line_2 || '');
    formData.append('city', storePayload.city || '');
    formData.append('state', storePayload.state || '');
    formData.append('country', storePayload.country || '');
    formData.append('postal_code', storePayload.postal_code || '0000');
    formData.append('latitude', storePayload.latitude || '0');
    formData.append('longitude', storePayload.longitude || '0');
    formData.append('average_price', storePayload.average_price || '');

    // Handle file uploads - IMPORTANT: Only append if it's actually a File object
    if (logoFile && logoFile instanceof File) {
      formData.append('logo', logoFile);
    }

    if (bannerFile && bannerFile instanceof File) {
      formData.append('banner_image', bannerFile);
    }

    return this.http.post(`${environment.apiBaseUrl}/dp/update-vendor/${vendorId}`, formData);
  }

  updateProfile(profilePayload: any, originalData?: any): Observable<any> {
    // Extract town, latitude, longitude from the original get-profile response
    const authData = originalData?.data || originalData;
    const town = authData?.user_details?.town || '';
    const latitude = authData?.user_details?.latitude || '';
    const longitude = authData?.user_details?.longitude || '';

    // Map form field names to API field names
    const apiPayload = {
      first_name: profilePayload.firstName || '',
      last_name: profilePayload.lastName || '',
      email: profilePayload.email || '',
      birth_date: profilePayload.birthDate || '',
      address: profilePayload.address || '',
      address2: profilePayload.address2 || '',
      city: profilePayload.city || '',
      town: town,
      latitude: latitude,
      longitude: longitude
    };
    
    return this.http.post(`${environment.apiBaseUrl}/contractor/update-profile`, apiPayload);
  }

  getContractorProfile(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/contractor/get-profile`);
  }

}
